import moment from "moment";

function normalizeRepeatingChars(input: string): string {
  return input.replace(/(.)\1{2,}/g, "$1");
}

function hasEnoughCommonChars(a: string, b: string): boolean {
  const sa = new Set(a);
  const sb = new Set(b);
  let count = 0;
  sa.forEach((c) => sb.has(c) && count++);
  return count >= 2;
}

function levenshtein(a: string, b: string): number {
  const dp = Array.from({ length: a.length + 1 }, () =>
    Array(b.length + 1).fill(0)
  );

  for (let i = 0; i <= a.length; i++) dp[i][0] = i;
  for (let j = 0; j <= b.length; j++) dp[0][j] = j;

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
  }
  return dp[a.length][b.length];
}

export function fuzzyMatchMonth(
  inputMonth: string,
  locale: string
): string | null {
  const months = moment
    .localeData(locale)
    .months()
    .map((m) => m.toLowerCase());

  let normalized = inputMonth.trim().toLowerCase();
  if (!normalized) return null;

  // 🔑 NEW: tolerate repeated chars
  normalized = normalizeRepeatingChars(normalized);

  // Exact match
  if (months.includes(normalized)) return normalized;

  // Prefix match (unique only)
  const byPrefix = months.filter((m) => m.startsWith(normalized));
  if (byPrefix.length === 1) return byPrefix[0];

  // Typo-tolerant (safe zone only)
  const scored = months
    .map((m) => ({
      month: m,
      score: levenshtein(normalized, m)
    }))
    .filter(
      ({ month, score }) =>
        score <= 2 &&
        normalized[0] === month[0] &&
        hasEnoughCommonChars(normalized, month)
    )
    .sort((a, b) => a.score - b.score);

  if (scored.length === 1) return scored[0].month;

  return null;
}

const DISPLAY_FORMAT = "DD MMMM YYYY";

export function parseFuzzyLocalizedDate(
  input: string,
  locale = "id"
): Date | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const parts = trimmed.split(/\s+/);
  if (parts.length < 3) return null;

  const day = parseInt(parts[0], 10);
  const year = parseInt(parts[parts.length - 1], 10);
  const monthRaw = parts.slice(1, -1).join(" ");

  if (
    Number.isNaN(day) ||
    Number.isNaN(year) ||
    day < 1 ||
    day > 31 ||
    year < 1000 ||
    year > 9999
  ) {
    return null;
  }

  const month = fuzzyMatchMonth(monthRaw, locale);
  if (!month) return null;

  const parsed = moment(`${day} ${month} ${year}`, "D MMMM YYYY", locale, true);
  return parsed.isValid() ? parsed.toDate() : null;
}

export function formatLocalizedDate(date: Date, locale = "id"): string {
  return moment(date)
    .locale(locale)
    .format(DISPLAY_FORMAT);
}
